import React, { useMemo, useState } from 'react';
import Card from './Card';
import Button from './Button';
import { AnnouncementItem } from '../../utils/announcements';

interface NoticeBannerProps {
  announcements: AnnouncementItem[];
}

const DISMISS_STORAGE_KEY = 'maru_notice_dismiss_until_v1';

const getDismissMap = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    return JSON.parse(raw) as Record<string, number>;
  } catch (error) {
    console.error('공지 숨김 정보 파싱 오류:', error);
    return {};
  }
};

const setDismissMap = (dismissMap: Record<string, number>) => {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(dismissMap));
  } catch (error) {
    console.error('공지 숨김 정보 저장 오류:', error);
  }
};

const NoticeBanner: React.FC<NoticeBannerProps> = ({ announcements }) => {
  const [dismissMap, setDismissMapState] = useState<Record<string, number>>(() => getDismissMap());

  const visibleAnnouncements = useMemo(() => {
    const now = Date.now();
    return announcements.filter((announcement) => {
      const dismissUntil = dismissMap[announcement.id];
      return !dismissUntil || dismissUntil <= now;
    });
  }, [announcements, dismissMap]);

  const handleDismiss = (announcementId: string, mode: 'today' | 'week') => {
    const now = new Date();
    const dismissUntil = mode === 'today'
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime()
      : now.getTime() + 7 * 24 * 60 * 60 * 1000;

    const nextMap = {
      ...dismissMap,
      [announcementId]: dismissUntil
    };
    setDismissMap(nextMap);
    setDismissMapState(nextMap);
  };

  if (visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <div style={{ width: '100%', marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {visibleAnnouncements.map((announcement) => (
        <Card
          key={announcement.id}
          padding="16px"
          gap="12px"
          background="var(--Color-background-normal, #FFFFFF)"
          style={{
            border: '1px solid var(--Color-line-normal, #F0F0F0)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
              <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--Color-text-strong, #0D0D0D)' }}>
                📢 {announcement.title}
              </div>
              <div style={{ fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-normal, #161616)', whiteSpace: 'pre-wrap' }}>
                {announcement.content}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                variant="outline"
                size="small"
                onClick={() => handleDismiss(announcement.id, 'today')}
              >
                오늘 하루 보지 않기
              </Button>
              <Button
                variant="outline"
                size="small"
                onClick={() => handleDismiss(announcement.id, 'week')}
              >
                일주일 보지 않기
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default NoticeBanner;
